import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { initMercadoPago, Payment } from '@mercadopago/sdk-react';
import { Navbar } from '../components/Navbar';
import { CheckCircle, Loader2, ShoppingBag, ArrowRight, Zap, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import confetti from 'canvas-confetti';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Unified Transparent Checkout - Mercado Pago Payment Brick
 * URL params:
 *   ?target=subscription&target_id=basic
 *   ?target=credit_pack&target_id=pack_25
 *   ?target=order&target_id=<order_id>
 */
export const Checkout = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const targetType = params.get('target');
  const targetId = params.get('target_id');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [intent, setIntent] = useState(null);      // {checkout_id, amount, description, public_key, target_meta}
  const [status, setStatus] = useState(null);      // approved | pending | in_process | rejected...
  const [statusDetail, setStatusDetail] = useState(null);
  const [pix, setPix] = useState(null);            // { qr_code, qr_code_base64 }
  const [boleto, setBoleto] = useState(null);      // { url, barcode }
  const [initialized, setInitialized] = useState(false);

  // Cria intent
  useEffect(() => {
    if (!targetType || !targetId) {
      setError('Parâmetros inválidos');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.post(
          `${API_URL}/checkout/create-intent`,
          { target_type: targetType, target_id: targetId },
          { withCredentials: true }
        );
        setIntent(res.data);
        if (res.data.public_key) {
          initMercadoPago(res.data.public_key, { locale: 'pt-BR' });
          setInitialized(true);
        } else {
          setError('Chave pública da MP não configurada');
        }
      } catch (e) {
        setError(e.response?.data?.detail || 'Falha ao iniciar checkout');
      } finally {
        setLoading(false);
      }
    })();
  }, [targetType, targetId]);

  // Polling de status quando PIX ou Boleto pendente
  const pollStatus = useCallback(async () => {
    if (!intent?.checkout_id) return;
    try {
      const res = await axios.get(`${API_URL}/checkout/${intent.checkout_id}/status`, { withCredentials: true });
      setStatus(res.data.status);
      setStatusDetail(res.data.status_detail);
      if (res.data.status === 'approved') {
        onApproved();
      }
    } catch (e) {
      // silencioso
    }
  }, [intent]);

  useEffect(() => {
    if (!pix && !boleto) return;
    if (status === 'approved' || status === 'rejected' || status === 'cancelled') return;
    const interval = setInterval(pollStatus, 4000);
    return () => clearInterval(interval);
  }, [pix, boleto, status, pollStatus]);

  const onApproved = () => {
    confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 } });
    setTimeout(() => {
      if (targetType === 'subscription') {
        navigate('/seller/dashboard', { replace: true });
      } else if (targetType === 'credit_pack') {
        navigate('/seller/dashboard', { replace: true });
      } else if (targetType === 'order') {
        navigate('/pedidos', { replace: true });
      }
    }, 2500);
  };

  // Handler do Payment Brick — envia formData para o backend criar o pagamento
  const handleSubmit = async ({ selectedPaymentMethod, formData }) => {
    try {
      const res = await axios.post(
        `${API_URL}/checkout/${intent.checkout_id}/pay`,
        { formData, selectedPaymentMethod },
        { withCredentials: true }
      );
      setStatus(res.data.status);
      setStatusDetail(res.data.status_detail);
      if (res.data.pix) setPix(res.data.pix);
      if (res.data.boleto) setBoleto(res.data.boleto);

      if (res.data.status === 'approved') {
        toast.success('Pagamento aprovado!');
        onApproved();
      } else if (res.data.status === 'rejected') {
        toast.error(`Pagamento recusado: ${res.data.status_detail || ''}`);
      } else if (res.data.pix) {
        toast.info('Escaneie o QR Code Pix abaixo para finalizar');
      } else if (res.data.boleto) {
        toast.info('Boleto gerado! Baixe abaixo.');
      } else {
        toast.info('Pagamento em processamento');
      }
      return res.data;
    } catch (e) {
      const msg = e.response?.data?.detail || 'Falha ao processar pagamento';
      toast.error(msg);
      throw e;
    }
  };

  const copyPix = () => {
    if (!pix?.qr_code) return;
    navigator.clipboard.writeText(pix.qr_code);
    toast.success('Código Pix copiado!');
  };

  // ─── Renderização ───────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        <Navbar />
        <div className="max-w-xl mx-auto mt-24 bg-red-950/30 border border-red-500/30 rounded-xl p-6 text-center">
          <h2 className="text-xl font-semibold text-red-300 mb-2">Não foi possível iniciar o checkout</h2>
          <p className="text-red-200 text-sm">{error}</p>
          <Button onClick={() => navigate(-1)} className="mt-4">Voltar</Button>
        </div>
      </div>
    );
  }

  const titleMap = {
    subscription: 'Ativar Assinatura',
    credit_pack: 'Comprar Créditos',
    order: 'Finalizar Pedido',
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-[1fr_400px] gap-8">
        {/* Coluna: Payment Brick */}
        <div>
          <div className="mb-6">
            <p className="text-indigo-300 text-sm font-medium mb-1">{titleMap[targetType] || 'Checkout'}</p>
            <h1 className="text-3xl font-bold" data-testid="checkout-title">{intent?.description}</h1>
            <p className="text-slate-400 text-sm mt-1">Pague com Pix, Cartão ou Boleto — tudo dentro do Needi.</p>
          </div>

          {status === 'approved' ? (
            <div className="bg-emerald-900/30 border border-emerald-500/40 rounded-2xl p-10 text-center" data-testid="checkout-approved">
              <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-emerald-200 mb-2">Pagamento aprovado!</h2>
              <p className="text-emerald-300/80 text-sm">Redirecionando…</p>
            </div>
          ) : pix ? (
            <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-6" data-testid="checkout-pix-panel">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-emerald-400" /> Pague com Pix
              </h2>
              {pix.qr_code_base64 && (
                <div className="bg-white rounded-lg p-4 w-64 h-64 mx-auto mb-4">
                  <img src={`data:image/png;base64,${pix.qr_code_base64}`} alt="QR Code Pix" className="w-full h-full object-contain" />
                </div>
              )}
              <div className="bg-slate-900 rounded-lg p-3 mb-3">
                <p className="text-xs text-slate-400 mb-1">Código Pix (copia e cola):</p>
                <p className="text-xs text-slate-200 font-mono break-all">{pix.qr_code}</p>
              </div>
              <Button onClick={copyPix} className="w-full" data-testid="copy-pix-btn">Copiar código Pix</Button>
              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-amber-300">
                <Loader2 className="w-4 h-4 animate-spin" /> Aguardando confirmação do pagamento…
              </div>
            </div>
          ) : boleto ? (
            <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-6" data-testid="checkout-boleto-panel">
              <h2 className="text-xl font-semibold mb-4">Boleto gerado</h2>
              <p className="text-slate-300 text-sm mb-4">Após o pagamento, o boleto compensa em até 2 dias úteis.</p>
              {boleto.barcode && (
                <div className="bg-slate-900 rounded-lg p-3 mb-3">
                  <p className="text-xs text-slate-400 mb-1">Linha digitável:</p>
                  <p className="text-xs text-slate-200 font-mono break-all">{boleto.barcode}</p>
                </div>
              )}
              <a href={boleto.url} target="_blank" rel="noreferrer" data-testid="boleto-download-link">
                <Button className="w-full">Baixar boleto <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </a>
            </div>
          ) : initialized && intent ? (
            <div className="bg-white rounded-2xl p-6" data-testid="checkout-brick-container">
              <Payment
                initialization={{
                  amount: intent.amount,
                  payer: {},
                }}
                customization={{
                  paymentMethods: {
                    creditCard: 'all',
                    debitCard: 'all',
                    bankTransfer: ['pix'],
                    ticket: 'all',
                    maxInstallments: 12,
                  },
                  visual: {
                    style: {
                      theme: 'default',
                    },
                  },
                }}
                onSubmit={handleSubmit}
                onError={(err) => {
                  console.error('Payment Brick error:', err);
                  toast.error('Erro no formulário de pagamento');
                }}
              />
            </div>
          ) : null}

          {status === 'rejected' && (
            <div className="mt-4 bg-red-900/30 border border-red-500/40 rounded-lg p-4" data-testid="checkout-rejected">
              <p className="text-red-200 text-sm">Pagamento recusado. {statusDetail}</p>
            </div>
          )}
        </div>

        {/* Coluna: Resumo */}
        <div>
          <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-6 sticky top-24" data-testid="order-summary">
            <div className="flex items-center gap-2 text-slate-400 text-xs uppercase font-semibold tracking-wider mb-4">
              <ShoppingBag className="w-4 h-4" /> Resumo
            </div>
            <div className="space-y-3 pb-4 border-b border-slate-700">
              <p className="text-white font-medium">{intent?.description}</p>
              {intent?.target_meta?.credits ? (
                <p className="text-sm text-slate-400">{intent.target_meta.credits} créditos</p>
              ) : null}
            </div>
            <div className="pt-4 space-y-2">
              <div className="flex justify-between text-sm text-slate-300">
                <span>Subtotal</span>
                <span>R$ {intent?.amount?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold text-white pt-2 border-t border-slate-700">
                <span>Total</span>
                <span data-testid="checkout-total">R$ {intent?.amount?.toFixed(2)}</span>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-slate-700 flex items-center gap-2 text-xs text-slate-400">
              <ShieldCheck className="w-4 h-4 text-emerald-400" /> Processado pelo Mercado Pago
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
